/**
 * Contrato alinhado à coluna Postgres `public.colaboradores.cargo` (jsonb).
 * v1: mapa plano por módulo legado.
 * v2: módulos com `v` (visão/sidebar) e `a` (ações com chaves curtas no JSON).
 */
import {
  COLLABORATOR_PERMISSION_MODULES,
  type PermissionModuleId,
  type PermissionActionId,
  getModuleByJsonKey,
  getModuleById,
} from "./collaboratorPermissionRegistry";

export const COLLABORATOR_CARGO_SCHEMA_VERSION = 2 as const;
export const COLLABORATOR_CARGO_SCHEMA_VERSION_LEGACY = 1 as const;

/** Chaves legadas v1 (UI antiga / compat). */
export const COLLABORATOR_PERMISSION_KEYS = [
  "dashboard",
  "pedidos",
  "cardapio",
  "agendamentos",
  "conversas",
  "financeiro",
  "colaboradores",
  "configuracoes",
] as const;

export type CollaboratorPermissionKey = (typeof COLLABORATOR_PERMISSION_KEYS)[number];

/** Estrutura interna normalizada (sempre após parse). */
export type CargoModuleState = {
  v: boolean;
  a: Partial<Record<PermissionActionId, boolean>>;
};

export type NormalizedCargoPermissions = Partial<Record<PermissionModuleId, CargoModuleState>>;

export type ColaboradorCargoJson =
  | {
      schemaVersion: 1;
      titulo: string;
      permissoes: Partial<Record<CollaboratorPermissionKey, boolean>>;
    }
  | {
      schemaVersion: 2;
      titulo: string;
      p: ColaboradorCargoJsonV2Stored["p"];
    };

/** Payload compacto v2 como gravado no banco (chaves curtas). */
export type ColaboradorCargoJsonV2Stored = {
  schemaVersion: 2;
  titulo: string;
  /** Módulos: chave curta → { v?: boolean, a?: Record<shortAction, boolean> } */
  p: Record<
    string,
    { v?: boolean; a?: Record<string, boolean> }
  >;
};

function emptyModuleState(): CargoModuleState {
  return { v: false, a: {} };
}

function allActionsTrue(moduleId: PermissionModuleId): Partial<Record<PermissionActionId, boolean>> {
  const mod = getModuleById(moduleId);
  if (!mod) return {};
  const out: Partial<Record<PermissionActionId, boolean>> = {};
  for (const act of mod.actions) {
    out[act.id] = true;
  }
  return out;
}

function allActionsValue(
  moduleId: PermissionModuleId,
  checked: boolean
): Partial<Record<PermissionActionId, boolean>> {
  const mod = getModuleById(moduleId);
  if (!mod) return {};
  const out: Partial<Record<PermissionActionId, boolean>> = {};
  for (const act of mod.actions) {
    out[act.id] = checked;
  }
  return out;
}

/** Checkbox da categoria: liga/desliga o módulo e todas as ações juntas. */
export function applyModuleViewToggle(
  norm: NormalizedCargoPermissions,
  moduleId: PermissionModuleId,
  checked: boolean
): NormalizedCargoPermissions {
  return {
    ...norm,
    [moduleId]: { v: checked, a: allActionsValue(moduleId, checked) },
  };
}

/** Converte permissões v1 planas para modelo normalizado v2. */
export function migrateV1FlatToNormalized(
  flat: Partial<Record<CollaboratorPermissionKey, boolean>>
): NormalizedCargoPermissions {
  const out: NormalizedCargoPermissions = {};

  const setMod = (id: PermissionModuleId, v: boolean, actions?: Partial<Record<PermissionActionId, boolean>>) => {
    const mod = getModuleById(id);
    if (!mod) return;
    const a: Partial<Record<PermissionActionId, boolean>> = {};
    if (v && actions === undefined) {
      Object.assign(a, allActionsTrue(id));
    } else if (actions) {
      Object.assign(a, actions);
    }
    out[id] = { v, a };
  };

  setMod("dashboard", !!flat.dashboard);
  const ped = !!flat.pedidos;
  setMod("pedidos", ped, ped ? undefined : {});

  setMod("company", !!flat.cardapio, {
    ver: !!flat.cardapio,
    editar_cardapio: !!flat.cardapio,
    editar_delivery: !!flat.cardapio,
    editar_dados_empresa: !!flat.cardapio,
  });

  setMod("agendamentos", !!flat.agendamentos, !!flat.agendamentos ? undefined : {});
  setMod("conversas", !!flat.conversas, !!flat.conversas ? undefined : {});
  setMod("financeiro", !!flat.financeiro, !!flat.financeiro ? undefined : {});

  const col = !!flat.colaboradores;
  setMod("colaboradores", col, col ? undefined : {});

  setMod("configuracoes", !!flat.configuracoes, !!flat.configuracoes ? undefined : {});

  // Módulos só v2 (default false se não existiam em v1)
  setMod("motoboys", false, {});
  setMod("manager", false, {});
  setMod("clientes", false, {});
  setMod("license", false, {});
  setMod("delivai_controle", false, {});

  const legacyAllTrue = COLLABORATOR_PERMISSION_KEYS.every((k) => flat[k] === true);
  if (legacyAllTrue) {
    for (const mod of COLLABORATOR_PERMISSION_MODULES) {
      out[mod.id] = { v: true, a: allActionsTrue(mod.id) };
    }
  }

  return out;
}

/** Serializa permissão normalizada para JSON mínimo v2 (`p` com chaves curtas). */
export function serializeNormalizedToStoredV2(norm: NormalizedCargoPermissions): ColaboradorCargoJsonV2Stored["p"] {
  const p: ColaboradorCargoJsonV2Stored["p"] = {};
  for (const mod of COLLABORATOR_PERMISSION_MODULES) {
    const state = norm[mod.id];
    if (!state) continue;
    const aOut: Record<string, boolean> = {};
    for (const act of mod.actions) {
      const val = state.a[act.id];
      if (typeof val === "boolean") {
        aOut[act.jsonKey] = val;
      }
    }
    const entry: { v?: boolean; a?: Record<string, boolean> } = {};
    if (state.v) {
      entry.v = true;
      if (Object.keys(aOut).length > 0) entry.a = aOut;
      p[mod.jsonKey] = entry;
    } else {
      p[mod.jsonKey] = { v: false };
    }
  }
  return p;
}

/** Desserializa `p` do banco para normalizado. */
export function parseStoredV2ToNormalized(p: Record<string, { v?: boolean; a?: Record<string, boolean> }>): NormalizedCargoPermissions {
  const out: NormalizedCargoPermissions = {};
  for (const [modShort, raw] of Object.entries(p)) {
    const mod =
      getModuleByJsonKey(modShort) ??
      getModuleById(modShort as PermissionModuleId);
    if (!mod) continue;
    const a: Partial<Record<PermissionActionId, boolean>> = {};
    if (raw.a && typeof raw.a === "object") {
      for (const act of mod.actions) {
        if (typeof raw.a[act.jsonKey] === "boolean") {
          a[act.id] = raw.a[act.jsonKey] as boolean;
        } else if (typeof raw.a[act.id] === "boolean") {
          a[act.id] = raw.a[act.id] as boolean;
        }
      }
    }
    const hasAnyAction = Object.values(a).some((v) => v === true);
    // v:false explícito permanece off — senão ações antigas religam a categoria.
    const v = raw.v === false ? false : raw.v === true || hasAnyAction;
    out[mod.id] = {
      v,
      a: v ? a : {},
    };
  }
  return out;
}

function isMotoboyCargoTitulo(titulo: string | undefined | null): boolean {
  const t = (titulo || "").trim().toLowerCase();
  return t === "entregador" || t === "motoboy";
}

function isWaiterCargoTitulo(titulo: string | undefined | null): boolean {
  const t = (titulo || "").trim().toLowerCase();
  return t === "garçom" || t === "garcom" || t === "atendente";
}

/**
 * Cargo antigo só tinha `pedidos`. Sem chave `pdv`/`delivery` no JSON,
 * hidrata as telas novas (motoboy não ganha PDV).
 */
export function expandPedidosIntoPdvDelivery(
  norm: NormalizedCargoPermissions,
  titulo?: string | null
): NormalizedCargoPermissions {
  const ped = norm.pedidos;
  if (!ped?.v) return norm;
  const next: NormalizedCargoPermissions = { ...norm };
  if (isMotoboyCargoTitulo(titulo)) {
    if (next.delivery === undefined) {
      next.delivery = { v: false, a: {} };
    }
    if (next.pdv === undefined) {
      next.pdv = { v: false, a: {} };
    }
    return next;
  }
  if (isWaiterCargoTitulo(titulo)) {
    if (next.pdv === undefined) {
      next.pdv = { v: true, a: { ...ped.a, despachar: false } };
    }
    if (next.delivery === undefined) {
      next.delivery = { v: false, a: {} };
    }
    return next;
  }
  if (next.pdv === undefined) {
    next.pdv = { v: true, a: { ...ped.a, despachar: false } };
  }
  if (next.delivery === undefined) {
    next.delivery = { v: true, a: { ...ped.a } };
  }
  return next;
}

export function getNormalizedPermissions(cargo: ColaboradorCargoJson | null | undefined): NormalizedCargoPermissions {
  if (!cargo || typeof cargo.titulo !== "string") {
    return migrateV1FlatToNormalized({});
  }
  if (cargo.schemaVersion === 2 && "p" in cargo && cargo.p && typeof cargo.p === "object") {
    return expandPedidosIntoPdvDelivery(parseStoredV2ToNormalized(cargo.p), cargo.titulo);
  }
  if (cargo.schemaVersion === 1 && "permissoes" in cargo) {
    return expandPedidosIntoPdvDelivery(
      migrateV1FlatToNormalized(cargo.permissoes || {}),
      cargo.titulo
    );
  }
  return migrateV1FlatToNormalized({});
}

export function parseColaboradorCargoJson(raw: unknown): ColaboradorCargoJson | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.titulo !== "string") return null;

  const sv = o.schemaVersion;
  if (sv === 2 && o.p && typeof o.p === "object") {
    return {
      schemaVersion: 2,
      titulo: o.titulo,
      p: o.p as ColaboradorCargoJsonV2Stored["p"],
    };
  }

  if (sv === 1 || sv === undefined) {
    const perm = o.permissoes;
    const permissoes: Partial<Record<CollaboratorPermissionKey, boolean>> = {};
    if (perm && typeof perm === "object") {
      for (const k of COLLABORATOR_PERMISSION_KEYS) {
        const v = (perm as Record<string, unknown>)[k];
        if (typeof v === "boolean") permissoes[k] = v;
      }
    }
    return {
      schemaVersion: 1,
      titulo: o.titulo,
      permissoes,
    };
  }

  return null;
}

/** Defaults granulares (v2) por título — restringe ações quando o flat legado daria “tudo ligado”. */
function defaultNormalizedOverridesForTitulo(titulo: string): NormalizedCargoPermissions | null {
  const t = (titulo || "").trim().toLowerCase();
  if (t === "entregador" || t === "motoboy") {
    return {
      dashboard: { v: true, a: { ver: true } },
      pedidos: {
        v: true,
        a: {
          ver: true,
          aceitar: false,
          cancelar: false,
          editar: false,
          despachar: false,
          finalizar: true,
          ver_historico: false,
          pedido_interno: false,
          imprimir: false,
        },
      },
      motoboys: { v: false, a: {} },
      conversas: { v: false, a: {} },
      company: { v: false, a: {} },
      agendamentos: { v: false, a: {} },
      financeiro: { v: false, a: {} },
      colaboradores: { v: false, a: {} },
      configuracoes: { v: false, a: {} },
      manager: { v: false, a: {} },
      clientes: { v: false, a: {} },
      license: { v: false, a: {} },
      delivai_controle: { v: false, a: {} },
      pdv: { v: false, a: {} },
      delivery: { v: false, a: {} },
    };
  }
  if (t === "cozinheiro") {
    return {
      pedidos: {
        v: true,
        a: {
          ver: true,
          aceitar: true,
          cancelar: false,
          editar: false,
          despachar: false,
          finalizar: true,
          ver_historico: true,
          pedido_interno: false,
          imprimir: true,
        },
      },
      pdv: {
        v: true,
        a: {
          ver: true,
          aceitar: true,
          cancelar: false,
          editar: false,
          finalizar: true,
          ver_historico: true,
          pedido_interno: false,
          imprimir: true,
        },
      },
      delivery: {
        v: true,
        a: {
          ver: true,
          aceitar: true,
          cancelar: false,
          editar: false,
          despachar: false,
          finalizar: true,
          ver_historico: true,
          pedido_interno: false,
          imprimir: true,
        },
      },
      motoboys: { v: false, a: {} },
      conversas: { v: false, a: {} },
    };
  }
  if (t === "garçom" || t === "garcom" || t === "atendente") {
    return {
      pedidos: {
        v: true,
        a: {
          ver: true,
          aceitar: true,
          cancelar: false,
          editar: true,
          despachar: false,
          finalizar: true,
          ver_historico: true,
          pedido_interno: true,
          imprimir: true,
        },
      },
      pdv: {
        v: true,
        a: {
          ver: true,
          aceitar: true,
          cancelar: false,
          editar: true,
          finalizar: true,
          ver_historico: true,
          pedido_interno: true,
          imprimir: true,
        },
      },
      delivery: { v: false, a: {} },
      motoboys: { v: false, a: {} },
      conversas: { v: false, a: {} },
      company: { v: false, a: {} },
      agendamentos: { v: false, a: {} },
      dashboard: { v: false, a: {} },
    };
  }
  return null;
}

/** Constrói cargo v2 com defaults por título e merges opcionais por módulo/ação. */
export function buildColaboradorCargoJson(
  titulo: string,
  overrides?: Partial<Record<CollaboratorPermissionKey, boolean>>,
  normalizedOverrides?: NormalizedCargoPermissions
): ColaboradorCargoJson {
  const baseFlat = defaultPermissionsForTitulo(titulo);
  const mergedFlat = { ...baseFlat, ...overrides };
  let norm = migrateV1FlatToNormalized(mergedFlat);
  const titleNorm = defaultNormalizedOverridesForTitulo(titulo);
  if (titleNorm) {
    norm = mergeNormalized(norm, titleNorm);
  }
  if (normalizedOverrides) {
    norm = mergeNormalized(norm, normalizedOverrides);
  }
  const p = serializeNormalizedToStoredV2(norm);
  return {
    schemaVersion: 2,
    titulo: titulo.trim() || "Colaborador",
    p,
  };
}

function mergeNormalized(
  base: NormalizedCargoPermissions,
  over: NormalizedCargoPermissions
): NormalizedCargoPermissions {
  const out: NormalizedCargoPermissions = { ...base };
  for (const [mid, st] of Object.entries(over) as [PermissionModuleId, CargoModuleState][]) {
    if (!st) continue;
    const prev = out[mid] || emptyModuleState();
    out[mid] = {
      v: st.v !== undefined ? st.v : prev.v,
      a: { ...prev.a, ...st.a },
    };
  }
  return out;
}

export function defaultPermissionsForTitulo(titulo: string): Partial<Record<CollaboratorPermissionKey, boolean>> {
  const t = (titulo || "").trim().toLowerCase();
  const all = (): Partial<Record<CollaboratorPermissionKey, boolean>> =>
    Object.fromEntries(COLLABORATOR_PERMISSION_KEYS.map((k) => [k, true])) as Partial<
      Record<CollaboratorPermissionKey, boolean>
    >;

  if (t === "admin" || t === "gerente") {
    return all();
  }
  const base: Partial<Record<CollaboratorPermissionKey, boolean>> = {
    dashboard: true,
    pedidos: true,
    cardapio: false,
    agendamentos: false,
    conversas: true,
    financeiro: false,
    colaboradores: false,
    configuracoes: false,
  };
  if (t === "entregador" || t === "motoboy") {
    // Flat legado: pedidos/dashboard on — ações finas em defaultNormalizedOverridesForTitulo.
    return { ...base, pedidos: true, dashboard: true, conversas: false };
  }
  if (t === "cozinheiro") {
    return { ...base, pedidos: true, cardapio: true };
  }
  if (t === "garçom" || t === "garcom" || t === "atendente") {
    return { ...base, pedidos: true, cardapio: false, agendamentos: false, conversas: false };
  }
  return base;
}

/** Para persistência: objeto mínimo v2. */
export function toStoredCargoJson(cargo: ColaboradorCargoJson): { schemaVersion: 2; titulo: string; p: ColaboradorCargoJsonV2Stored["p"] } {
  if (cargo.schemaVersion === 2) {
    return { schemaVersion: 2, titulo: cargo.titulo, p: cargo.p };
  }
  const norm = getNormalizedPermissions(cargo);
  return {
    schemaVersion: 2,
    titulo: cargo.titulo,
    p: serializeNormalizedToStoredV2(norm),
  };
}

function moduleActionGranted(
  norm: NormalizedCargoPermissions,
  moduleId: PermissionModuleId,
  actionId: PermissionActionId
): boolean {
  const m = norm[moduleId];
  if (!m || !m.v) return false;
  if (actionId === "ver") {
    if (typeof m.a.ver === "boolean") {
      return m.a.ver === true;
    }
    return true;
  }
  return m.a[actionId] === true;
}

export function hasPermission(
  norm: NormalizedCargoPermissions,
  moduleId: PermissionModuleId,
  actionId: PermissionActionId
): boolean {
  if (moduleId === "pdv" || moduleId === "delivery") {
    if (norm[moduleId] !== undefined) {
      return moduleActionGranted(norm, moduleId, actionId);
    }
    return moduleActionGranted(norm, "pedidos", actionId);
  }
  if (moduleId === "pedidos") {
    if (moduleActionGranted(norm, "pedidos", actionId)) return true;
    if (actionId === "despachar") {
      return moduleActionGranted(norm, "delivery", actionId);
    }
    return (
      moduleActionGranted(norm, "pdv", actionId) ||
      moduleActionGranted(norm, "delivery", actionId)
    );
  }
  return moduleActionGranted(norm, moduleId, actionId);
}

export function canViewModule(norm: NormalizedCargoPermissions, moduleId: PermissionModuleId): boolean {
  if (moduleId === "pedidos") {
    return (
      norm.pedidos?.v === true ||
      norm.pdv?.v === true ||
      norm.delivery?.v === true
    );
  }
  if ((moduleId === "pdv" || moduleId === "delivery") && norm[moduleId] !== undefined) {
    return norm[moduleId]?.v === true;
  }
  return norm[moduleId]?.v === true;
}

/**
 * Se o `cargo` veio do webhook com `p` vazio (v2) mas o título é Admin/Gerente,
 * recalcula permissões padrão completas para não travar a sidebar.
 */
export function ensureCargoJsonForSidebar(cargo: ColaboradorCargoJson): ColaboradorCargoJson {
  const norm = getNormalizedPermissions(cargo);
  const hasAnyView = COLLABORATOR_PERMISSION_MODULES.some((m) => norm[m.id]?.v === true);
  if (hasAnyView) return cargo;
  const t = (cargo.titulo || "").trim().toLowerCase();
  if (t === "admin" || t === "gerente") {
    return buildColaboradorCargoJson(cargo.titulo);
  }
  return cargo;
}

/** Monta `ColaboradorCargoJson` v2 a partir do estado normalizado (editor de permissões). */
export function buildCargoFromNormalized(
  titulo: string,
  norm: NormalizedCargoPermissions
): ColaboradorCargoJson {
  return {
    schemaVersion: 2,
    titulo: titulo.trim() || "Colaborador",
    p: serializeNormalizedToStoredV2(norm),
  };
}
